import * as RadixPopover from '@radix-ui/react-popover';

import { cn } from '../../utils/cn';
import type { DropdownTriggerProps } from './Dropdown.types';
import { useDropdownContext } from './DropdownContext';

/**
 * Compound Dropdown trigger — **web** implementation.
 *
 * We use Radix `Anchor` (not `Trigger`) + a ref'd `<span>` wrapper so ANY
 * trigger works without forwarding a ref, and the render-prop's `onPress` is
 * the single source of truth for toggling. `DropdownContent`'s
 * `onInteractOutside` guard reads the same ref to suppress Radix's
 * outside-dismiss when the pointer-down lands on the trigger — otherwise the
 * dismiss + the trigger's own toggle would fight and the menu would
 * "close then reopen".
 *
 * The wrapper also stops the trigger click from bubbling — see the handler.
 */
export function DropdownTrigger({
  children,
  fullWidth = false,
  className,
  webProps,
}: DropdownTriggerProps) {
  const { open, toggle, anchorRef } = useDropdownContext('DropdownTrigger');

  return (
    <RadixPopover.Anchor asChild>
      <span
        ref={anchorRef}
        className={cn('inline-flex', fullWidth && 'w-full', className)}
        {...webProps}
        onClick={(event) => {
          (webProps?.onClick as React.MouseEventHandler<HTMLSpanElement> | undefined)?.(
            event,
          );
          // A trigger click means "open this menu" and NOTHING else. Menus are
          // routinely embedded in something clickable — a deck-card <Link>, a
          // list row — and without this the click also fires that action.
          //
          // BOTH calls are load-bearing, for two different ancestors:
          //
          //  - preventDefault: an <a href> (react-router <Link>) navigates via
          //    its browser DEFAULT ACTION, which stopPropagation does NOT
          //    cancel — only this does. It also makes react-router's own
          //    handler stand down: Link is
          //    `if (onClick) onClick(e); if (!e.defaultPrevented) internalOnClick(e)`,
          //    and internalOnClick is what navigates.
          //  - stopPropagation: a plain `onClick` ancestor (a clickable row /
          //    card div) has no default action to cancel, so only this stops it.
          //
          // stopPropagation ALONE is worse than nothing inside a <Link>: it
          // skips react-router's handler — the one that would have called
          // preventDefault — leaving the raw anchor to hard-navigate the
          // document. Do not drop the preventDefault.
          //
          // It has to live here: the render-prop hands call sites
          // `onPress: () => void` with no event, so they cannot guard this
          // themselves without wrapping the whole Dropdown in a span.
          event.preventDefault();
          event.stopPropagation();
        }}>
        {children({ open, onPress: toggle })}
      </span>
    </RadixPopover.Anchor>
  );
}
